import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { i18n } from '../../../../../../i18n/i18n';
import { COLORS } from '../../../../../../constants/constants';
import CustomActions from '../../../../../../components/CustomActions/CustomActions';
import { IconAdd, IconChecked, IconCopy, IconImageSimilarity } from '../../../../../../images';
import { copyToClipboard } from '../../../../../../utils/communication';
import { dateFormatter } from '../../../../../../utils/formatters';
import fallbackUserSquare from '../../../../../../images/fallbacks/user-fallback-square.svg';
import { Tooltip, SmartImage } from '@cellebrite/saas-ui-common';

interface MatchCardProps {
    findInput?: string;
    platform: any;
    account: any;
    onCheckedChange: any;
    setClickedAccount: any;
}
const MatchCard: React.FC<MatchCardProps> = ({ findInput, platform, account, onCheckedChange, setClickedAccount }) => {
    const [cardActive, setCardActive] = useState(false);
    const [checked, setChecked] = useState(false);
    const ellipsisStyles = { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' };
    const getInfo = () => {
        let result;
        if (account.description) {
            result = account.description;
        } else if (account.location) {
            result = account.location;
        } else if (account.workplace) {
            result = account.workplace;
        } else if (account.website) {
            result = account.website;
        } else if (account.followersCount >= 0) {
            result = `${account.followersCount} ${i18n.translate('Followers')}`;
        } else if (account.followingCount >= 0) {
            result = `${account.followingCount} ${i18n.translate('Followers')}`;
        } else if (account.dateCreated) {
            result = dateFormatter(account.dateCreated);
        } else if (account.postCount >= 0) {
            result = `${account.postCount} ${i18n.translate('Posts')}`;
        }
        return result;
    };
    const regFindInput = findInput?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const getMarkedText = (text) => {
        let result = text;
        if (text && findInput?.length) {
            result = text.replaceAll(
                new RegExp(regFindInput, 'gi'),
                '<mark style="background-color: #FFECB2">$&</mark>',
            );
        }
        return result;
    };
    useEffect(() => {
        const updatedChecked = account.checked ? true : false;
        setChecked(updatedChecked);
    }, [account]);
    return (
        <Box
            className={`${
                account.added
                    ? 'border-color-p background-color-plst'
                    : checked
                      ? 'border-color-b background-color-blst'
                      : 'border-color-mho'
            } ${checked || account.added ? '' : 'background-color-hover-mho'}`}
            sx={{
                pb: '8px',
                borderRadius: '8px',
                border: '1px solid',
                display: 'flex',
                minWidth: 154,
                maxWidth: 154,
                height: 256,
                position: 'relative',
            }}
            data-testid="matches-search-information"
            onClick={(e) => (e['actionsClicked'] ? null : setClickedAccount(account))}
            onMouseEnter={() => setCardActive(true)}
            onMouseLeave={() => setCardActive(false)}
        >
            <Box
                sx={{
                    width: '100%',
                    position: 'relative',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {!account.added && (cardActive || checked) && (
                    <Tooltip
                        placement="right"
                        content={<Typography>{i18n.translate(checked ? 'Selected' : 'Select')}</Typography>}
                    >
                        <Box
                            sx={{
                                'position': 'absolute',
                                'left': '0',
                                'm': 1,
                                'zIndex': 1,
                                'backgroundColor': checked ? COLORS.BRAND : COLORS.MUTED_DARK,
                                'borderRadius': '80px',
                                '&:hover': { backgroundColor: COLORS.MUTED_SHADY },
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                setChecked(!checked);
                                if (onCheckedChange) {
                                    onCheckedChange(!checked, account);
                                }
                            }}
                        >
                            <IconButton sx={{ width: 24, height: 24 }}>
                                {checked ? <IconChecked fill="#FFF" /> : <IconAdd fill="#FFF" />}
                            </IconButton>
                        </Box>
                    </Tooltip>
                )}
                {account.isMatch && (
                    <Tooltip
                        placement="bottom-start"
                        content={
                            <Box>
                                <Typography variant="s14w500">{i18n.translate('AI facial similarity')}</Typography>
                                <Typography>
                                    {i18n.translate(
                                        'AI facial similarity suggests that this image is very similar to the search image',
                                    )}
                                </Typography>
                            </Box>
                        }
                    >
                        <Box
                            sx={{
                                backgroundColor: '#121E287A',
                                width: 24,
                                height: 24,
                                position: 'absolute',
                                top: 110,
                                m: 1,
                                zIndex: 1,
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <IconImageSimilarity />
                        </Box>
                    </Tooltip>
                )}
                {account.added && (
                    <Box
                        sx={{
                            position: 'absolute',
                            m: 1,
                            zIndex: 1,
                            width: 57,
                            height: 24,
                            backgroundColor: '#CBF5C8',
                            borderRadius: '4px',
                        }}
                    >
                        <Typography variant="s14w500" sx={{ ml: 1, mr: 1 }}>
                            {i18n.translate('Added')}
                        </Typography>
                    </Box>
                )}
                <SmartImage
                    fallbackImg={fallbackUserSquare}
                    src={account.profileLocalPicture}
                    containerStyles={{
                        height: `152px`,
                        width: `152px`,
                        alignSelf: 'center',
                        border: `${account.isMatch ? 3 : 0}px solid ${COLORS.WARNING_LIGHTEST}`,
                        borderTopLeftRadius: `${account.isMatch ? 4 : 8}px`,
                        borderTopRightRadius: `${account.isMatch ? 4 : 8}px`,
                    }}
                />
                <Typography
                    sx={{ ...{ mt: 1, ml: 1, mr: 1 }, ...ellipsisStyles }}
                    variant="s12w700"
                    className="color-bd"
                    title={account.name}
                    dangerouslySetInnerHTML={{
                        __html: getMarkedText(account.name),
                    }}
                ></Typography>
                <Typography
                    sx={{ ...{ ml: 1, mr: 1 }, ...ellipsisStyles }}
                    variant="s12w500"
                    className="color-md"
                    title={account.userName}
                    dangerouslySetInnerHTML={{
                        __html: getMarkedText(account.userName),
                    }}
                ></Typography>
                <Typography
                    sx={{
                        ml: 1,
                        mr: 1,
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        height: '30px',
                    }}
                    variant="s10w400"
                    className="color-md"
                    title={getInfo()}
                    dangerouslySetInnerHTML={{
                        __html: getMarkedText(getInfo()),
                    }}
                ></Typography>
            </Box>
            {cardActive && (
                <CustomActions
                    containerStyles={{
                        'position': 'absolute',
                        'right': 10,
                        'top': 10,
                        '& .button-container': { backgroundColor: COLORS.MUTED_DARK, borderRadius: '80px' },
                        '& .button-container:hover': { backgroundColor: COLORS.MUTED_SHADY },
                        '& .button-container .icon': { fill: '#FFF' },
                    }}
                    actions={[
                        {
                            name: i18n.translate('Copy ID'),
                            icon: <IconCopy className="fill-md" />,
                            onClick: () => copyToClipboard(account.id),
                        },
                        {
                            name: i18n.translate('Go to the Profile'),
                            icon: <IconCopy className="fill-md" />,
                            disabled: !account.url,
                            onClick: () => window.open(account.url, '_blank', 'noopener,noreferrer'),
                        },
                    ]}
                />
            )}
        </Box>
    );
};

export default MatchCard;
